import { useState, useEffect, useCallback } from 'react';

const INSTALL_DISMISSED_KEY = 'vibary_install_dismissed';

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isIOSSafari, setIsIOSSafari] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem(INSTALL_DISMISSED_KEY) === 'true';
  });

  useEffect(() => {
    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Detect iOS Safari (not in standalone mode, not Chrome/Firefox on iOS)
    const isStandalone = window.navigator.standalone === true;
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome|CriOS|FxiOS/.test(navigator.userAgent);
    setIsIOSSafari(isIOSDevice && isSafari && !isStandalone);

    // Check if already installed (standalone mode)
    const isStandaloneMode =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsInstalled(isStandaloneMode);

    // Listen for beforeinstallprompt (Android/Desktop Chrome, Edge)
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Trigger the native install prompt (Android/Desktop)
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setIsInstallable(false);

    return outcome === 'accepted';
  }, [deferredPrompt]);

  // Dismiss the install prompt (don't show again this session or permanently)
  const dismissInstall = useCallback((permanent = false) => {
    if (permanent) {
      localStorage.setItem(INSTALL_DISMISSED_KEY, 'true');
      setIsDismissed(true);
    }
  }, []);

  // Reset dismissed state (for settings)
  const resetDismissed = useCallback(() => {
    localStorage.removeItem(INSTALL_DISMISSED_KEY);
    setIsDismissed(false);
  }, []);

  // Check if we should show the install prompt
  const canShowInstallPrompt = !isInstalled && !isDismissed && (isInstallable || isIOSSafari);

  return {
    isInstallable,
    isInstalled,
    isIOS,
    isIOSSafari,
    isDismissed,
    canShowInstallPrompt,
    promptInstall,
    dismissInstall,
    resetDismissed,
  };
};
